import { SendEventParams, useUserEventLogger } from '@repo/tracking-service';
import { BaseTopNavigation } from '@repo/ui';
import { ColorPalette, IconType, TextButtonStateType, TopNavigation } from '@zenterprise-inc/ui';
import { BasicTopNavigationProps } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { useSetAtom } from 'jotai';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { HTMLAttributes, JSX, ReactNode, useEffect } from 'react';

import Notice from './Notice';

import {
  HeaderBackButton,
  HeaderBznavRefundLogo,
  HeaderCloseButton,
  HeaderExitTextButton,
  HeaderInquiryIconButton,
  HeaderInquiryTextButton,
  HeaderMoreIconButton,
  HeaderShareTextButton
} from '@/components/layout/LayoutItems.tsx';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link.ts';
import { topNavigationInquiryLinkAtom } from '@/lib/stores/common.ts';
import { DefaultLayoutClickEvents, LeftItemType, LogoLinkType, RightItemType } from '@/lib/types/layout.ts';

type ClickEvent = {
  name: string;
  attributes?: SendEventParams['attributes'];
};

type LayoutRightIconModel = {
  icon: IconType;
  color?: ColorPalette;
  onClick?: () => void;
  path?: string;
  clickEvent?: ClickEvent;
};

type LayoutProps = {
  title?: ReactNode;
  leftIcon?: {
    icon: BasicTopNavigationProps['leftIcon'];
    onClick?: () => void;
    clickEvent?: ClickEvent;
  };
  rightIcons?: LayoutRightIconModel[];
  rightTextButton?: {
    label: string;
    state?: TextButtonStateType;
    onClick?: () => void;
    path?: string;
    clickEvent?: ClickEvent;
  };
  className?: HTMLAttributes<HTMLDivElement>['className'];
  mainClassName?: HTMLAttributes<HTMLDivElement>['className'];
  backgroundColor?: string;
  children: ReactNode;
  footerContent?: ReactNode;
  isNoticeVisible?: boolean;
};

/**
 * @deprecated
 */
const Layout = ({
  children,
  leftIcon,
  rightIcons,
  rightTextButton,
  className,
  mainClassName,
  backgroundColor,
  footerContent,
  isNoticeVisible = true,
  ...props
}: LayoutProps) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();

  const handleTopNavigationButtonAction = (path?: string, callback?: () => void, clickEvent?: ClickEvent) => {
    if (clickEvent) {
      sendClickEvent(clickEvent.name, clickEvent.attributes);
    }
    if (path) {
      router.push(`${path}?orgAlias=${router.query?.orgAlias}`);
      return;
    }

    callback?.();
  };

  const topNavigationIcons = rightIcons?.map((item: LayoutRightIconModel) => ({
    icon: item.icon,
    color: item.color,
    onClick: () => {
      handleTopNavigationButtonAction(item.path, item.onClick, item.clickEvent);
    }
  }));

  const topNavigationTextButton = {
    label: rightTextButton?.label || '',
    state: rightTextButton?.state,
    onClick: () => {
      handleTopNavigationButtonAction(rightTextButton?.path, rightTextButton?.onClick, rightTextButton?.clickEvent);
    }
  };

  return (
    <div className={cn('w-full min-h-screen flex flex-col', className, { ['bg-gray-50']: backgroundColor === '#F3F7F8' })}>
      {backgroundColor && (
        <Head>
          <meta name={'theme-color'} content={backgroundColor} />
          <meta name="apple-mobile-web-app-status-bar-style" content={backgroundColor} />
        </Head>
      )}
      <TopNavigation.Basic
        className={cn('z-[2] w-full', {
          ['!bg-gray-50']: backgroundColor === '#F3F7F8'
        })}
        onLeftClick={() => {
          if (leftIcon?.icon === 'back') {
            if (leftIcon?.clickEvent) {
              sendClickEvent(leftIcon.clickEvent.name, leftIcon.clickEvent.attributes);
            }
            router.back();
            return;
          }
          handleTopNavigationButtonAction(undefined, leftIcon?.onClick, leftIcon?.clickEvent);
        }}
        leftIcon={leftIcon?.icon}
        rightIcons={rightIcons && topNavigationIcons}
        rightTextButton={rightTextButton && topNavigationTextButton}
        leftIconColor={'gray.800'}
        {...props}
      />
      {isNoticeVisible && <Notice />}
      <main className={cn('mx-auto w-full container-medium flex-1', mainClassName)}>{children}</main>
      {footerContent && <footer>{footerContent}</footer>}
    </div>
  );
};

export const DefaultLayout = ({
  children,
  footerContent,
  leftItem,
  rightItems,
  isNoticeVisible = true,
  logoLinkType,
  inquiryLink,
  headerBackgroundColor,
  mainClassName,
  clickEvents
}: {
  children: ReactNode;
  footerContent?: ReactNode;
  leftItem?: LeftItemType;
  rightItems?: RightItemType[];
  isNoticeVisible?: boolean;
  logoLinkType?: LogoLinkType;
  inquiryLink?: string;
  headerBackgroundColor?: string;
  mainClassName?: HTMLAttributes<HTMLDivElement>['className'];
  clickEvents?: DefaultLayoutClickEvents;
}) => {
  const setInquiryLink = useSetAtom(topNavigationInquiryLinkAtom);

  const leftItemComponents: Record<LeftItemType, JSX.Element> = {
    back: <HeaderBackButton clickEvent={clickEvents?.back} />,
    close: <HeaderCloseButton clickEvent={clickEvents?.close} />,
    primaryLogo: <HeaderBznavRefundLogo color={'primary'} logoLinkType={logoLinkType} clickEvent={clickEvents?.primaryLogo} />,
    blackLogo: <HeaderBznavRefundLogo color={'black'} logoLinkType={logoLinkType} clickEvent={clickEvents?.blackLogo} />,
    whiteLogo: <HeaderBznavRefundLogo color={'white'} logoLinkType={logoLinkType} clickEvent={clickEvents?.whiteLogo} />,
    grayLogo: <HeaderBznavRefundLogo color={'gray'} logoLinkType={logoLinkType} clickEvent={clickEvents?.grayLogo} />
  };

  const rightItemComponents: Record<RightItemType, JSX.Element> = {
    exitTextButton: <HeaderExitTextButton key={'exit'} clickEvent={clickEvents?.exitTextButton} />,
    inquiryTextButton: <HeaderInquiryTextButton key={'inquiryText'} clickEvent={clickEvents?.inquiryTextButton} />,
    inquiryIconButton: <HeaderInquiryIconButton key={'inquiryIcon'} clickEvent={clickEvents?.inquiryIconButton} />,
    shareTextButton: <HeaderShareTextButton key={'share'} clickEvent={clickEvents?.shareTextButton} />,
    moreIconButton: <HeaderMoreIconButton key={'more'} clickEvent={clickEvents?.moreIconButton} />
  };

  useEffect(() => {
    setInquiryLink(inquiryLink ?? SUPPORT_BOT_LINK.default);
  }, [inquiryLink, setInquiryLink]);

  return (
    <>
      {headerBackgroundColor && (
        <Head>
          <meta name={'theme-color'} content={headerBackgroundColor} />
          <meta name="apple-mobile-web-app-status-bar-style" content={headerBackgroundColor} />
        </Head>
      )}
      <header>
        <BaseTopNavigation
          leftAction={(leftItem && leftItemComponents[leftItem]) || <></>}
          rightAction={<>{rightItems?.map((item) => rightItemComponents[item])}</>}
          bg={!!headerBackgroundColor}
          bgColor={headerBackgroundColor}
        />
      </header>
      {isNoticeVisible && <Notice />}
      <main className={`w-full flex-1 flex flex-col items-center ${mainClassName}`}>{children}</main>
      {footerContent && <footer className={'w-full'}>{footerContent}</footer>}
    </>
  );
};

export default Layout;
